import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateHazardAction } from '../Hazard.hooks';
import { BaseHazardActionsCardProps, HazardAction } from '../Hazard.interface';

const HazardActionInfoCard: React.FC<BaseHazardActionsCardProps> = (
  props: BaseHazardActionsCardProps
) => {
  const {
    title = 'Details',
    hazardId,
    hazardAction,
    hazardActionVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = hazardActionVersion || hazardAction;
  const initialValues: Partial<HazardAction> = {
    title: initialData.title || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/hazards/${hazardId}/actions/${hazardAction.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedHazardAction,
    isUpdatingHazardAction: isUpdating,
    updateHazardActionError: updateError,
    updateHazardAction
  } = useUpdateHazardAction(hazardId, hazardAction.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(hazardAction.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(hazardAction.task);

  // notify parent when a user need info is updated
  useEffect(() => {
    if (updatedHazardAction) {
      onChange?.(updatedHazardAction);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedHazardAction]);

  useEffect(() => {
    if (updatedTask) {
      hazardAction.task = Object.assign(hazardAction.task || {}, updatedTask);
      onChange?.(hazardAction);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <InlineFormik
      initialValues={initialValues}
      isSubmitting={isUpdating}
      error={updateError}
      onSubmit={(values: Partial<HazardAction>) => {
        updateHazardAction(values);
        onChange?.(values);
      }}
    >
      <Card style={hidden ? { display: 'none' } : undefined}>
        <CardTitle>
          <SubjectIcon />
          {enableTitleRedirect ? (
            <Link className='link' to={getPath()}>
              {title}
            </Link>
          ) : (
            title
          )}
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineTextField
              name='title'
              label='Title'
              placeholder='Enter title for Hazard Action'
              disabled={
                Boolean(hazardActionVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText='Provide an overview of the action.'
              disabled={
                Boolean(hazardActionVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={
                hazardActionVersion?.attachments ||
                hazardAction.task.attachments ||
                []
              }
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (hazardAction.task) {
                  hazardAction.task.attachments = attachments;
                }
                onChange?.(hazardAction);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};

export default HazardActionInfoCard;
